import React, { useState, useEffect, useCallback } from 'react';
import BroadcastPanel from '../components/BroadcastPanel';

const C = {
  primary: '#0F6E56', primaryBg: '#E1F5EE', border: '#E3E1D9',
  text: '#1F1E1B', muted: '#5F5E5A', surface: '#F7F5F0',
  gold: '#91600C', danger: '#C0392B',
};

function authHeaders() {
  const t = localStorage.getItem('token') || '';
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Token ${t}` } : {}) };
}

function ago(iso) {
  if (!iso) return '—';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 300) return 'just now';
  if (s < 3600) return Math.floor(s / 60) + 'm';
  if (s < 86400) return Math.floor(s / 3600) + 'h';
  const d = Math.floor(s / 86400);
  return d < 30 ? d + 'd' : d < 365 ? Math.floor(d / 30) + 'mo' : Math.floor(d / 365) + 'y';
}

function Tag({ tag }) {
  if (!tag) return null;
  return (
    <span style={{ fontSize: 9.5, fontWeight: 700, padding: '1px 7px', borderRadius: 9,
                   background: tag.color + '18', color: tag.color, whiteSpace: 'nowrap' }}>
      {tag.label}{tag.scope && tag.scope !== 'all' ? ' · ' + tag.scope : ''}
    </span>
  );
}

const btn = { fontSize: 11, padding: '4px 9px', border: '1px solid ' + C.border,
              borderRadius: 6, background: '#fff', cursor: 'pointer', color: C.muted };

/* ---------- USERS ---------- */
function UsersTab() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    fetch('/api/admin-panel/users/?q=' + encodeURIComponent(q), { headers: authHeaders() })
      .then((r) => r.json()).then(setD).catch(() => {});
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const act = async (username, action, extra) => {
    try {
      const res = await fetch('/api/admin-panel/users/' + username + '/', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify(Object.assign({ action: action }, extra || {})),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Failed');
      load();
    } catch (e) { alert(e.message); }
  };

  const suspend = (u) => {
    const reason = window.prompt('Suspend @' + u.username + '?\n\nReason (they will see this):');
    if (reason === null) return;
    act(u.username, 'suspend', { reason: reason });
  };

  const del = (u) => {
    const c = window.prompt('DELETE @' + u.username + ' permanently?\n\nThis cannot be undone.\nType the username to confirm:');
    if (!c) return;
    act(u.username, 'delete', { confirm: c });
  };

  const setRole = (u) => {
    const role = window.prompt('Role for @' + u.username + '\n\nuser / moderator / admin / superadmin', u.role || 'user');
    if (!role) return;
    const keys = ['can_moderate_posts', 'can_manage_users', 'can_manage_communities',
                  'can_manage_admins', 'can_change_settings'];
    const perms = {};
    keys.forEach((k) => { perms[k] = window.confirm(u.username + ': allow ' + k.replace(/_/g, ' ') + '?'); });
    act(u.username, 'set_role', { role: role, permissions: perms });
  };

  if (!d) return <p style={{ color: C.muted, padding: '1.5rem 0', textAlign: 'center' }}>Loading…</p>;
  if (d.error) return <p style={{ color: C.danger, padding: '1.5rem 0', textAlign: 'center' }}>{d.error}</p>;

  return (
    <>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search username…"
        style={{ width: '100%', padding: '8px 11px', fontSize: 13, border: '1px solid ' + C.border,
                 borderRadius: 8, marginBottom: 10, boxSizing: 'border-box' }} />
      <p style={{ fontSize: 11, color: C.muted, margin: '0 0 8px' }}>{d.total} users</p>

      {d.users.map((u) => (
        <div key={u.username} style={{ background: '#fff',
             border: '1px solid ' + (u.is_active ? C.border : C.danger),
             borderRadius: 9, padding: '9px 11px', marginBottom: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              @{u.username}
              <Tag tag={u.tag} />
              {!u.is_active && <span style={{ fontSize: 9.5, color: C.danger, fontWeight: 700 }}>SUSPENDED</span>}
            </span>
            <span style={{ fontSize: 10.5, color: C.muted }}>
              {u.country ? u.country + ' · ' : ''}seen {ago(u.last_seen)}
            </span>
          </div>

          {u.modules.length > 0 && (
            <p style={{ margin: '4px 0 0', fontSize: 10.5, color: C.muted }}>
              {u.modules.map((m) => m.label + ' (' + m.rights.join(', ') + ')').join(' · ')}
            </p>
          )}
          {u.suspended_reason && (
            <p style={{ margin: '4px 0 0', fontSize: 10.5, color: C.danger }}>Reason: {u.suspended_reason}</p>
          )}

          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 7 }}>
            <button style={btn} onClick={() => setOpen(open === u.username ? null : u.username)}>
              {'\u{1F4CB}'} Audit
            </button>
            {d.me.can_manage_admins && !u.protected && (
              <button style={btn} onClick={() => setRole(u)}>{'\u{1F451}'} Role: {u.role}</button>
            )}
            {d.me.can_manage_users && !u.protected && (
              u.is_active
                ? <button style={Object.assign({}, btn, { color: C.gold, borderColor: C.gold })} onClick={() => suspend(u)}>Suspend</button>
                : <button style={Object.assign({}, btn, { color: C.primary, borderColor: C.primary })} onClick={() => act(u.username, 'unsuspend')}>Unsuspend</button>
            )}
            {d.me.is_super_admin && !u.protected && (
              <button style={Object.assign({}, btn, { color: C.danger, borderColor: C.danger })} onClick={() => del(u)}>Delete</button>
            )}
          </div>

          {open === u.username && <UserAudit username={u.username} />}
        </div>
      ))}
    </>
  );
}

function UserAudit({ username }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    fetch('/api/activity/user/' + username + '/', { headers: authHeaders() })
      .then((r) => r.json()).then(setD).catch(() => {});
  }, [username]);

  if (!d) return <p style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>Loading audit…</p>;
  if (d.error) return <p style={{ fontSize: 11, color: C.danger, marginTop: 8 }}>{d.error}</p>;

  const c = d.completion;
  return (
    <div style={{ marginTop: 9, borderTop: '1px solid ' + C.surface, paddingTop: 8 }}>
      <p style={{ margin: 0, fontSize: 11.5, fontWeight: 700 }}>Profile {c.percent}% ({c.done}/{c.total})</p>
      <div style={{ background: C.surface, borderRadius: 4, height: 5, overflow: 'hidden', margin: '4px 0 6px' }}>
        <div style={{ width: c.percent + '%', height: '100%',
             background: c.percent >= 100 ? C.primary : c.percent >= 60 ? C.gold : C.danger }} />
      </div>
      {c.missing.length > 0 && (
        <p style={{ margin: '0 0 7px', fontSize: 10.5, color: C.muted }}>Missing: {c.missing.join(', ')}</p>
      )}
      <p style={{ margin: '0 0 5px', fontSize: 11, color: C.muted }}>
        today {d.activity_counts.today} · week {d.activity_counts.week} · month {d.activity_counts.month} · all {d.activity_counts.all}
      </p>
      {d.recent_actions.slice(0, 12).map((a) => (
        <div key={a.id} style={{ fontSize: 10.5, color: C.muted, padding: '2px 0' }}>
          <span style={{ fontFamily: 'monospace' }}>{a.action}</span>
          {a.target_user && <span style={{ color: C.gold }}> {'\u2192'} @{a.target_user}</span>}
          <span> · {ago(a.at)}</span>
        </div>
      ))}
      {d.actions_on_this_user.length > 0 && (
        <>
          <p style={{ margin: '8px 0 4px', fontSize: 11, fontWeight: 700, color: C.danger }}>
            Actions taken ON this user
          </p>
          {d.actions_on_this_user.slice(0, 10).map((a) => (
            <div key={a.id} style={{ fontSize: 10.5, color: C.muted, padding: '2px 0' }}>
              @{a.user} · <span style={{ fontFamily: 'monospace' }}>{a.action}</span> · {ago(a.at)}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/* ---------- MODULE ADMINS ---------- */
function ModulesTab() {
  const [d, setD] = useState(null);
  const [uname, setUname] = useState('');
  const [mod, setMod] = useState('');
  const [rights, setRights] = useState([]);

  const load = useCallback(() => {
    fetch('/api/admin-panel/platform-admins/', { headers: authHeaders() })
      .then((r) => r.json()).then(setD).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!uname || !mod) { alert('Pick a username and a module.'); return; }
    try {
      const res = await fetch('/api/admin-panel/platform-admin/set/', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ username: uname, module: mod, rights: rights }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Failed');
      alert(rights.length ? '@' + uname + ' is now an admin for ' + mod
                          : 'Removed @' + uname + ' from ' + mod);
      setUname(''); setRights([]); load();
    } catch (e) { alert(e.message); }
  };

  if (!d) return <p style={{ color: C.muted, padding: '1.5rem 0', textAlign: 'center' }}>Loading…</p>;
  if (d.error) return <p style={{ color: C.danger, padding: '1.5rem 0', textAlign: 'center' }}>{d.error}</p>;

  const inp = { padding: '7px 10px', fontSize: 12.5, border: '1px solid ' + C.border, borderRadius: 7 };

  return (
    <>
      <div style={{ background: '#fff', border: '1px solid ' + C.border, borderRadius: 9, padding: 11, marginBottom: 12 }}>
        <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700 }}>Assign a module admin</p>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 8 }}>
          <input value={uname} onChange={(e) => setUname(e.target.value)} placeholder="username"
                 style={Object.assign({}, inp, { flex: '1 1 150px' })} />
          <select value={mod} onChange={(e) => setMod(e.target.value)}
                  style={Object.assign({}, inp, { flex: '1 1 150px' })}>
            <option value="">Choose module…</option>
            {d.available_modules.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </div>
        <p style={{ margin: '0 0 5px', fontSize: 11, color: C.muted }}>Rights (untick all = remove)</p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 9 }}>
          {d.available_rights.map((r) => {
            const on = rights.indexOf(r.key) >= 0;
            return (
              <button key={r.key} title={r.label}
                onClick={() => setRights(on ? rights.filter((x) => x !== r.key) : rights.concat([r.key]))}
                style={Object.assign({}, btn, {
                  background: on ? C.primaryBg : '#fff',
                  color: on ? C.primary : C.muted,
                  borderColor: on ? C.primary : C.border,
                  fontWeight: on ? 700 : 400 })}>
                {on ? '\u2713 ' : ''}{r.key}
              </button>
            );
          })}
        </div>
        <button onClick={save} style={Object.assign({}, btn, {
          background: C.primary, color: '#fff', borderColor: C.primary,
          fontWeight: 700, padding: '7px 16px', fontSize: 12.5 })}>Save</button>
        <p style={{ margin: '7px 0 0', fontSize: 10, color: C.muted }}>
          Their profile must be 100% complete first.
        </p>
      </div>

      {d.modules.length === 0 ? (
        <p style={{ fontSize: 12.5, color: C.muted, textAlign: 'center', padding: '1rem 0' }}>No module admins yet.</p>
      ) : d.modules.map((m) => (
        <div key={m.module} style={{ background: '#fff', border: '1px solid ' + C.border,
             borderRadius: 9, padding: '9px 11px', marginBottom: 6 }}>
          <p style={{ margin: '0 0 5px', fontSize: 13, fontWeight: 700 }}>{m.label}</p>
          {m.admins.map((a) => (
            <p key={a.username} style={{ margin: '2px 0', fontSize: 11.5, color: C.muted }}>
              @{a.username} — <span style={{ color: C.primary }}>{a.rights.join(', ')}</span>
            </p>
          ))}
        </div>
      ))}
    </>
  );
}

/* ---------- APPLICATIONS ---------- */
function AppsTab() {
  const [d, setD] = useState(null);
  const [status, setStatus] = useState('pending');

  const load = useCallback(() => {
    fetch('/api/admin-applications/?status=' + status, { headers: authHeaders() })
      .then((r) => r.json()).then(setD).catch(() => {});
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const review = async (a, action) => {
    const note = window.prompt(action === 'approve' ? 'Note (optional)' : 'Why not? (they will see this)');
    if (note === null) return;
    try {
      const res = await fetch('/api/admin-applications/' + a.id + '/', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ action: action, note: note, rights: a.rights }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Failed');
      load();
    } catch (e) { alert(e.message); }
  };

  if (!d) return <p style={{ color: C.muted, padding: '1.5rem 0', textAlign: 'center' }}>Loading…</p>;
  if (d.error) return <p style={{ color: C.danger, padding: '1.5rem 0', textAlign: 'center' }}>{d.error}</p>;

  return (
    <>
      <div style={{ display: 'flex', gap: 5, marginBottom: 10, flexWrap: 'wrap' }}>
        {['pending', 'approved', 'rejected', 'all'].map((s) => (
          <button key={s} onClick={() => setStatus(s)} style={Object.assign({}, btn, {
            background: status === s ? C.primaryBg : '#fff',
            color: status === s ? C.primary : C.muted,
            fontWeight: status === s ? 700 : 400 })}>{s}</button>
        ))}
      </div>

      {d.applications.length === 0 ? (
        <p style={{ fontSize: 12.5, color: C.muted, textAlign: 'center', padding: '1.5rem 0' }}>Nothing here.</p>
      ) : d.applications.map((a) => (
        <div key={a.id} style={{ background: '#fff', border: '1px solid ' + C.border,
             borderRadius: 9, padding: '10px 12px', marginBottom: 7 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>@{a.username} {'\u2192'} {a.module}</span>
            <span style={{ fontSize: 10.5, fontWeight: 700,
                  color: a.completion_now >= 100 ? C.primary : C.danger }}>
              profile {a.completion_now}%
            </span>
          </div>
          <p style={{ margin: '4px 0', fontSize: 11.5, color: C.muted }}>
            Rights: {a.rights.join(', ') || '—'} · {a.hours_per_week}h/week · {ago(a.applied)} ago
          </p>
          <p style={{ margin: '5px 0', fontSize: 12, color: C.text }}>{a.why}</p>
          {a.experience && <p style={{ margin: '4px 0', fontSize: 11.5, color: C.muted }}>Experience: {a.experience}</p>}

          {a.status === 'pending' && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button onClick={() => review(a, 'approve')} style={Object.assign({}, btn, {
                background: C.primary, color: '#fff', borderColor: C.primary, fontWeight: 700 })}>Approve</button>
              <button onClick={() => review(a, 'reject')} style={Object.assign({}, btn, {
                color: C.danger, borderColor: C.danger })}>Reject</button>
            </div>
          )}
          {a.status !== 'pending' && (
            <p style={{ margin: '5px 0 0', fontSize: 10.5, color: C.muted }}>
              {a.status} by @{a.reviewed_by}
            </p>
          )}
        </div>
      ))}
    </>
  );
}

/* ---------- ACTIVITY ---------- */
function ActivityTab() {
  const [d, setD] = useState(null);
  const [period, setPeriod] = useState('today');
  const [adminOnly, setAdminOnly] = useState(false);
  const [onUsers, setOnUsers] = useState(false);

  const load = useCallback(() => {
    let q = 'period=' + period;
    if (adminOnly) q += '&admin_only=1';
    if (onUsers) q += '&on_users=1';
    fetch('/api/activity/feed/?' + q, { headers: authHeaders() })
      .then((r) => r.json()).then(setD).catch(() => {});
  }, [period, adminOnly, onUsers]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <p style={{ color: C.muted, padding: '1.5rem 0', textAlign: 'center' }}>Loading…</p>;
  if (d.error) return <p style={{ color: C.danger, padding: '1.5rem 0', textAlign: 'center' }}>{d.error}</p>;

  return (
    <>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 9 }}>
        {['today', 'week', 'month', 'year', 'all'].map((p) => (
          <button key={p} onClick={() => setPeriod(p)} style={Object.assign({}, btn, {
            background: period === p ? C.primaryBg : '#fff',
            color: period === p ? C.primary : C.muted,
            fontWeight: period === p ? 700 : 400 })}>{p}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 10 }}>
        <button onClick={() => setAdminOnly(!adminOnly)} style={Object.assign({}, btn, {
          background: adminOnly ? '#FDF0DD' : '#fff', color: adminOnly ? C.gold : C.muted,
          fontWeight: adminOnly ? 700 : 400 })}>Admin actions only</button>
        <button onClick={() => setOnUsers(!onUsers)} style={Object.assign({}, btn, {
          background: onUsers ? '#FDF0DD' : '#fff', color: onUsers ? C.gold : C.muted,
          fontWeight: onUsers ? 700 : 400 })}>On users only</button>
      </div>

      <p style={{ fontSize: 11.5, color: C.muted, margin: '0 0 8px' }}>{d.total} actions</p>

      {d.by_user.length > 0 && (
        <div style={{ background: C.surface, borderRadius: 8, padding: '8px 11px', marginBottom: 10 }}>
          <p style={{ margin: '0 0 5px', fontSize: 11, fontWeight: 700 }}>Most active</p>
          {d.by_user.slice(0, 6).map((r) => (
            <div key={r.username} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, padding: '2px 0' }}>
              <span>@{r.username}</span><span style={{ color: C.muted }}>{r.count}</span>
            </div>
          ))}
        </div>
      )}

      {d.logs.map((a) => (
        <div key={a.id} style={{ background: '#fff',
             border: '1px solid ' + (a.is_admin_action ? C.gold : C.border),
             borderRadius: 7, padding: '6px 10px', marginBottom: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11.5 }}>
              <b>@{a.user}</b> <span style={{ fontFamily: 'monospace', color: C.primary }}>{a.action}</span>
              {a.target_user && <span style={{ color: C.gold, fontWeight: 700 }}> {'\u2192'} @{a.target_user}</span>}
            </span>
            <span style={{ fontSize: 10, color: C.muted }}>{a.method} {a.status} · {ago(a.at)}</span>
          </div>
        </div>
      ))}
    </>
  );
}

/* ---------- MAIN ---------- */
export default function AdminPanel({ user }) {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('users');

  useEffect(() => {
    fetch('/api/admin-panel/whoami/', { headers: authHeaders() })
      .then((r) => r.json()).then(setMe).catch(() => setMe({ is_admin: false }));
  }, []);

  const toggleMode = async () => {
    const next = !me.acting_as_user;
    if (!window.confirm(next
      ? 'Switch to USER mode?\n\nYour free access stops — real payments, real 5% commission.'
      : 'Switch back to SUPER ADMIN mode?')) return;
    try {
      const res = await fetch('/api/admin-panel/mode/', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ acting_as_user: next }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Failed');
      alert(j.note);
      window.location.reload();
    } catch (e) { alert(e.message); }
  };

  if (!user) return <div style={{ padding: 30, textAlign: 'center' }}><h2>Login required</h2></div>;
  if (!me) return <div style={{ padding: 30, textAlign: 'center', color: C.muted }}>Checking…</div>;
  if (!me.is_admin) return <div style={{ padding: 30, textAlign: 'center' }}><h2>Admins only</h2></div>;

  const tabBtn = (k, label) => (
    <button key={k} onClick={() => setTab(k)} style={{
      fontSize: 12.5, padding: '7px 12px', border: 'none', background: 'none', cursor: 'pointer',
      borderBottom: tab === k ? '2px solid ' + C.primary : '2px solid transparent',
      color: tab === k ? C.primary : C.muted, fontWeight: tab === k ? 700 : 400 }}>{label}</button>
  );

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '1.1rem 0.85rem 2rem', fontFamily: 'sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 20, margin: 0 }}>{'\u{1F6E1}\uFE0F'} Admin Panel</h1>
        <span style={{ fontSize: 11.5, color: C.muted, display: 'flex', gap: 6, alignItems: 'center' }}>
          @{me.username} <Tag tag={me.tag} />
        </span>
      </div>

      {me.is_super_admin && (
        <div style={{
          background: me.acting_as_user ? '#FDF0DD' : C.primaryBg,
          border: '1px solid ' + (me.acting_as_user ? C.gold : C.primary),
          borderRadius: 9, padding: '9px 12px', margin: '10px 0 4px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12 }}>
            {me.acting_as_user
              ? 'Acting as a normal user — real payments, real commission'
              : 'Super admin mode — free access everywhere'}
          </span>
          <button onClick={toggleMode} style={Object.assign({}, btn, {
            fontWeight: 700,
            color: me.acting_as_user ? C.primary : C.gold,
            borderColor: me.acting_as_user ? C.primary : C.gold })}>
            Switch to {me.acting_as_user ? 'super admin' : 'user'}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '10px 0' }}>
        <a href="/admin/wallet" style={Object.assign({}, btn, {
          textDecoration: 'none', color: C.primary, borderColor: C.primary, fontWeight: 700 })}>Wallet</a>
        <a href="/admin-dashboard" style={Object.assign({}, btn, { textDecoration: 'none' })}>Dashboard</a>
        <a href="/admin" style={Object.assign({}, btn, { textDecoration: 'none' })}>Old panel</a>
      </div>

      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid ' + C.border, marginBottom: 12, flexWrap: 'wrap' }}>
        {tabBtn('users', 'Users')}
        {tabBtn('modules', 'Module admins')}
        {tabBtn('apps', 'Applications')}
        {tabBtn('activity', 'Activity')}
        {tabBtn('broadcast', '\u{1F4E2} Broadcast')}
      </div>

      {tab === 'users' && <UsersTab />}
      {tab === 'modules' && <ModulesTab />}
      {tab === 'apps' && <AppsTab />}
      {tab === 'activity' && <ActivityTab />}
      {tab === 'broadcast' && <BroadcastPanel />}
    </div>
  );
}
